/**
 * Single Product Page
 * Handles booking price integration with TM Extra Product Options
 */

import stickyAside from '../global/sticky-aside';

const product = {
  _initialized: false,
  _formStateInterval: null,
  _mutationTimeout: null,
  _finalTotalVisibilityInterval: null,
  _priceUpdateQueue: [],
  _isProcessingQueue: false,
  _lastKnownBookingPrice: 0,
  _lastKnownBasePrice: 0,
  _lastKnownOptionsTotal: 0,
  _selectors: {
    cart: 'form.cart',
    totals: '.tm-extra-product-options-totals',
    totalsDL: 'dl.tm-extra-product-options-totals',
    epoTotals: '#tm-epo-totals',
    finalTotal: '.tm-final-totals .price.amount.final',
    feesTotal: '.tm-fee-totals .price.amount',
    optionsTotal: '.tm-options-totals .price.amount',
    bookingPrice: '.booking_price',
    tmepoFields: '.tm-extra-product-options input, .tm-extra-product-options select',
    galleryLightboxLinks: '[data-product-gallery-lightbox] a',
    gallery: '.woocommerce-product-gallery',
    galleryThumbsTrack: '.flex-control-thumbs',
    galleryThumbs: '.flex-control-thumbs li',
  },
  _galleryThumbsVisible: 4,
  _galleryThumbsOffsetProperty: '--product-gallery-thumbs-offset',

  init() {
    if (this._initialized) {
      return;
    }

    this._initialized = true;
    this._bindProductGalleryLightbox();
    this._bindProductGalleryThumbs();
    stickyAside.init();

    if (!jQuery(this._selectors.cart).length) {
      return;
    }

    this.bindEvents();
    this._startFinalTotalVisibilityCheck();

    setTimeout(() => {
      const bookingPrice = this._getBookingPrice();
      if (bookingPrice.price > 0) {
        this._queuePriceUpdate('init', bookingPrice.price, bookingPrice.formatted);
      }
    }, 100);
  },

  _bindProductGalleryLightbox() {
    const galleryParams = window.wc_single_product_params;

    if (
      !galleryParams ||
      !galleryParams.photoswipe_enabled ||
      !galleryParams.flexslider_enabled
    ) {
      return;
    }

    jQuery(document).on(
      'click.productGalleryLightbox',
      this._selectors.galleryLightboxLinks,
      (event) => {
        const $trigger = jQuery(event.currentTarget)
          .closest('.woocommerce-product-gallery')
          .find('.woocommerce-product-gallery__trigger')
          .first();

        if (!$trigger.length) {
          return;
        }

        event.preventDefault();
        $trigger.trigger('click');
      }
    );
  },

  _bindProductGalleryThumbs() {
    const $galleries = jQuery(this._selectors.gallery);

    if (!$galleries.length) {
      return;
    }

    $galleries.each((index, gallery) => {
      const $gallery = jQuery(gallery);
      const bind = () => this._initProductGalleryThumbsWindow($gallery);

      if ($gallery.data('flexslider')) {
        bind();
        return;
      }

      $gallery.one('wc-product-gallery-after-init', bind);
    });
  },

  _initProductGalleryThumbsWindow($gallery) {
    const slider = $gallery.data('flexslider');

    if (!slider || slider.s7ThumbsWindowBound) {
      return;
    }

    slider.s7ThumbsWindowBound = true;

    const visible = this._galleryThumbsVisible;
    const offsetProperty = this._galleryThumbsOffsetProperty;
    const trackSelector = this._selectors.galleryThumbsTrack;
    const thumbsSelector = this._selectors.galleryThumbs;
    const originalBefore = slider.vars.before;
    let start = 0;

    const update = (target) => {
      const track = $gallery.find(trackSelector).get(0);
      const total = $gallery.find(thumbsSelector).length;

      if (!track) {
        return;
      }

      if (total <= visible) {
        start = 0;
      } else if (target < start) {
        start = target;
      } else if (target >= start + visible) {
        start = target - visible + 1;
      }

      start = Math.max(0, Math.min(start, total - visible));
      track.style.setProperty(offsetProperty, start);
    };

    slider.vars.before = (currentSlider) => {
      if (typeof originalBefore === 'function') {
        originalBefore(currentSlider);
      }

      update(currentSlider.animatingTo);
    };

    update(slider.currentSlide);
  },

  _startFinalTotalVisibilityCheck() {
    if (this._finalTotalVisibilityInterval) {
      clearInterval(this._finalTotalVisibilityInterval);
    }

    // Check every 200ms if final total is visible and make it visible if not
    this._finalTotalVisibilityInterval = setInterval(() => {
      const $finalTotal = jQuery(this._selectors.finalTotal);
      if ($finalTotal.length) {
        const $finalTotalRow = $finalTotal.closest('dd').parent();
        if ($finalTotalRow.length && $finalTotalRow.is(':hidden')) {
          $finalTotalRow.show();
          $finalTotalRow.css('display', '');
          const $finalTotalDT = $finalTotalRow.find('dt.tm-final-totals');
          const $finalTotalDD = $finalTotalRow.find('dd.tm-final-totals');
          if ($finalTotalDT.length) {
            $finalTotalDT.show();
            $finalTotalDT.css('display', '');
          }
          if ($finalTotalDD.length) {
            $finalTotalDD.show();
            $finalTotalDD.css('display', '');
          }

          const $totals = jQuery(this._selectors.totals);
          if ($totals.length) {
            $totals.show();
            const $epoTotals = jQuery(this._selectors.epoTotals);
            if ($epoTotals.length) {
              $epoTotals[0].style.display = 'block';
            }
          }
        }
      } else {
        const $totals = jQuery(this._selectors.totals);
        if ($totals.length) {
          this._ensureFinalTotalVisible($totals);
        }
      }
    }, 200);
  },

  bindEvents() {
    const $cart = jQuery(this._selectors.cart);
    if (!$cart.length) {
      return;
    }

    this._setupFormStateMonitoring($cart);
    this._interceptFormBlocking($cart);
    this._bindBookingPriceUpdates($cart);
    this._bindTMEPOUpdates();
    this._bindFormSubmit($cart);
  },

  _setupFormStateMonitoring($cart) {
    const startMonitoring = () => {
      if (this._formStateInterval) {
        return;
      }

      this._formStateInterval = setInterval(() => {
        const opacity = parseFloat($cart.css('opacity'));
        const cursor = $cart.css('cursor');
        const blockedSince = $cart.data('blocked-since') || Date.now();

        if (opacity < 1 || cursor === 'wait') {
          if (!$cart.data('blocked-since')) {
            $cart.data('blocked-since', blockedSince);
          } else if (Date.now() - blockedSince > 1000) {
            $cart.css({ opacity: '1', cursor: 'auto' }).removeData('blocked-since');
          }
        } else {
          $cart.removeData('blocked-since');
        }
      }, 500);
    };

    return startMonitoring;
  },

  _interceptFormBlocking($cart) {
    const originalFadeTo = jQuery.fn.fadeTo;
    const originalCss = jQuery.fn.css;
    const startMonitoring = this._setupFormStateMonitoring($cart);
    const isCartForm = (el) => el.is(this._selectors.cart) || el.find(this._selectors.cart).length > 0;

    jQuery.fn.fadeTo = function(...args) {
      if (isCartForm(this) && args[1]) {
        const opacity = parseFloat(args[1]);
        if (opacity < 1) {
          startMonitoring();
        } else if (opacity >= 1) {
          $cart.removeData('blocked-since');
        }
      }
      return originalFadeTo.apply(this, args);
    };

    jQuery.fn.css = function(...args) {
      const result = originalCss.apply(this, args);
      if (isCartForm(this) && args[0] && typeof args[0] === 'object') {
        const css = args[0];
        if (css.opacity !== undefined || css.cursor !== undefined) {
          if (css.opacity && parseFloat(css.opacity) < 1) {
            startMonitoring();
          } else if (css.opacity && parseFloat(css.opacity) >= 1) {
            $cart.removeData('blocked-since');
          }
        }
      }
      return result;
    };
  },

  _bindBookingPriceUpdates($cart) {
    $cart.on('update_price', (e, response) => {
      try {
        let bookingPrice = 0;
        let bookingPriceFormatted = '';

        if (response && response.fragments) {
          const fragments = response.fragments;
          bookingPrice = parseFloat(fragments.booking_price) || 0;
          bookingPriceFormatted = fragments.booking_price_formatted || '';

          if (!bookingPriceFormatted && bookingPrice > 0) {
            bookingPriceFormatted = this.formatPrice(bookingPrice);
          }

          const $epoTotals = jQuery(this._selectors.epoTotals);
          if ($epoTotals.length && bookingPrice > 0) {
            $epoTotals.data('booking-price', bookingPrice);
            if (bookingPriceFormatted) {
              $epoTotals.data('booking-price-formatted', bookingPriceFormatted);
            }
          }

          this._queuePriceUpdate('update_price', bookingPrice, bookingPriceFormatted);
        }
      } catch (error) {
        // Silent fail in production
      }

      // Handle cart opacity/cursor (keep existing behavior)
      [300, 600, 1000, 1500].forEach((delay) => {
        setTimeout(() => {
          const opacity = parseFloat($cart.css('opacity'));
          const cursor = $cart.css('cursor');
          if (opacity < 1 || cursor === 'wait') {
            $cart.css({ opacity: '1', cursor: 'auto' }).removeData('blocked-since');
          }
        }, delay);
      });
    });
  },

  _bindTMEPOUpdates() {
    jQuery(document).on('tm-epo-update tc-epo-update', () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const bookingPrice = this._getBookingPrice();
            if (bookingPrice.price > 0) {
              this._queuePriceUpdate('tm-epo-update', bookingPrice.price, bookingPrice.formatted);
            }
          } catch (error) {
            // Silent fail in production
          }
        });
      });
    });

    jQuery(document).on('change', this._selectors.tmepoFields, (event) => {
      try {
        const $field = jQuery(event.target);
        const $epoTotals = jQuery(this._selectors.epoTotals);

        // For checkbox/radio, try to update options total immediately
        if ($epoTotals.length && ($field.is(':checkbox') || $field.is(':radio'))) {
          const isChecked = $field.is(':checked') || $field.prop('checked');
          let fieldPrice = 0;
          const priceAttr = $field.data('price') || $field.attr('data-price');
          if (priceAttr) {
            fieldPrice = parseFloat(priceAttr) || 0;
          } else {
            const $container = $field.closest('.tmcp-field-wrap, .tm-epo-field, label');
            const $priceElement = $container.find('.tm-epo-field-price, .price, [data-price]');
            if ($priceElement.length) {
              const priceText = $priceElement.text() || $priceElement.attr('data-price') || $priceElement.data('price');
              if (priceText) {
                fieldPrice = this.parsePriceFromText(priceText);
              }
            }
          }

          if (fieldPrice > 0.01) {
            const currentStored = parseFloat($epoTotals.data('options-total')) || 0;
            if (isChecked) {
              $epoTotals.data('options-total', currentStored + fieldPrice);
            } else {
              $epoTotals.data('options-total', Math.max(0, currentStored - fieldPrice));
            }
          }
        }

        const bookingPrice = this._getBookingPrice();
        if (bookingPrice.price > 0) {
          this._queuePriceUpdate('change', bookingPrice.price, bookingPrice.formatted);
        }
      } catch (error) {
        // Silent fail in production
      }
    });

    const $epoTotals = jQuery(this._selectors.epoTotals);
    const $body = jQuery('body');

    const observer = new MutationObserver((mutations) => {
      // Check if final total is being hidden or removed
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const $target = jQuery(mutation.target);
          if ($target.hasClass('tm-final-totals') || $target.closest('.tm-final-totals').length) {
            const $finalTotal = jQuery(this._selectors.finalTotal);
            if ($finalTotal.length) {
              const $finalTotalRow = $finalTotal.closest('dd').parent();
              if ($finalTotalRow.length && $finalTotalRow.is(':hidden')) {
                $finalTotalRow.show();
                $finalTotalRow.css('display', '');
                const $finalTotalDT = $finalTotalRow.find('dt.tm-final-totals');
                const $finalTotalDD = $finalTotalRow.find('dd.tm-final-totals');
                if ($finalTotalDT.length) {
                  $finalTotalDT.show();
                  $finalTotalDT.css('display', '');
                }
                if ($finalTotalDD.length) {
                  $finalTotalDD.show();
                  $finalTotalDD.css('display', '');
                }
              }
            }
          }
        } else if (mutation.type === 'childList') {
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const $node = jQuery(node);
              if ($node.hasClass('tm-final-totals') || $node.find('.tm-final-totals').length) {
                // Final total was removed - the periodic check will handle making it visible again
              }
            }
          });
        }
      });

      if (this._mutationTimeout) {
        cancelAnimationFrame(this._mutationTimeout);
      }
      this._mutationTimeout = requestAnimationFrame(() => {
        this._mutationTimeout = null;
        this._onDOMChange();
      });
    });

    if ($epoTotals.length) {
      observer.observe($epoTotals[0], {
        childList: true,
        subtree: true,
      });
    } else if ($body.length) {
      observer.observe($body[0], {
        childList: true,
        subtree: true,
      });
    }

    this._setupFinalTotalObserver();

    setTimeout(() => {
      this._onDOMChange();
    }, 500);
  },

  _setupFinalTotalObserver() {
    const checkFinalTotal = () => {
      const $finalTotal = jQuery(this._selectors.finalTotal);
      if ($finalTotal.length) {
        const bookingPrice = this._getBookingPrice();
        if (bookingPrice.price > 0) {
          this._verifyAndRestorePrice(bookingPrice.price);
        }
      }
    };

    const setupObserver = () => {
      const $finalTotal = jQuery(this._selectors.finalTotal);
      if ($finalTotal.length && !this._finalTotalObserver) {
        this._finalTotalObserver = new MutationObserver((mutations) => {
          const hasRelevantChange = mutations.some((mutation) => {
            return (
              mutation.type === 'characterData' ||
              (mutation.type === 'childList' && mutation.target === $finalTotal[0])
            );
          });

          if (hasRelevantChange && !this._isProcessingQueue) {
            requestAnimationFrame(() => {
              checkFinalTotal();
            });
          }
        });

        this._finalTotalObserver.observe($finalTotal[0], {
          characterData: true,
          childList: true,
          subtree: true,
        });
      }
    };

    setupObserver();
    setTimeout(setupObserver, 1000);
  },


  _ensureTotalsContainer($epoTotals) {
    let $dl = $epoTotals.find('dl.tm-extra-product-options-totals').first();

    if (!$dl.length) {
      $dl = jQuery('<dl class="tm-extra-product-options-totals tm-custom-price-totals"></dl>');
      $epoTotals.append($dl);
    }

    let $existingFeesDT = $epoTotals.find('dt.tm-fee-totals').first();
    let $existingFeesDD = $epoTotals.find('dd.tm-fee-totals').first();

    if ($existingFeesDT.length && !$dl.find('dt.tm-fee-totals').length) {
      if (!$existingFeesDT.closest('dl').length) {
        $dl.prepend($existingFeesDT);
      }
    }
    if ($existingFeesDD.length && !$dl.find('dd.tm-fee-totals').length) {
      if (!$existingFeesDD.closest('dl').length) {
        const $finalTotalDT = $dl.find('dt.tm-final-totals').first();
        if ($finalTotalDT.length) {
          $existingFeesDD.insertBefore($finalTotalDT);
        } else {
          $dl.append($existingFeesDD);
        }
      }
    }

    let $finalTotalDD = $dl.find('dd.tm-final-totals');
    let $finalTotalDT = $dl.find('dt.tm-final-totals');

    if (!$finalTotalDD.length || !$finalTotalDT.length) {
      if (!$finalTotalDT.length) {
        $finalTotalDT = jQuery('<dt class="tm-final-totals">Totaalbedrag inclusief opties</dt>');
        $dl.append($finalTotalDT);
      }
      if (!$finalTotalDD.length) {
        $finalTotalDD = jQuery('<dd class="tm-final-totals"><span class="price amount final">0,00</span></dd>');
        $dl.append($finalTotalDD);
      }
    }

    const storedOptionsTotal = parseFloat($epoTotals.data('options-total')) || 0;
    if (storedOptionsTotal > 0 && !$dl.find('dt.tm-fee-totals').length) {
      const formattedOptionsTotal = this.formatPrice(storedOptionsTotal);
      const $feesDT = jQuery('<dt class="tm-fee-totals">Extra opties</dt>');
      const $feesDD = jQuery('<dd class="tm-fee-totals"><span class="price amount fees"></span></dd>');
      $feesDD.find('.price.amount').html(formattedOptionsTotal);

      if ($finalTotalDT.length) {
        $feesDT.insertBefore($finalTotalDT);
        $feesDD.insertAfter($feesDT);
      } else {
        $dl.append($feesDT);
        $dl.append($feesDD);
      }
    }

    return $dl;
  },

  _onDOMChange() {
    try {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      let $totals = jQuery(this._selectors.totals);

      if (!$totals.length && $epoTotals.length) {
        this._ensureTotalsContainer($epoTotals);
        $totals = jQuery(this._selectors.totals);
      }

      if (!$totals.length) {
        return;
      }

      this._ensureFinalTotalVisible($totals);

      if (!$epoTotals.data('price')) {
        const $finalTotal = jQuery(this._selectors.finalTotal);
        if ($finalTotal.length) {
          const currentTotal = this.parsePriceFromText($finalTotal.text());
          const bookingPrice = this._getBookingPrice();
          if (currentTotal > 0) {
            if (bookingPrice.price > 0) {
              const optionsTotal = this.parsePriceFromText($totals.find('.tm-fee-totals .price.amount').text()) ||
                                  this.parsePriceFromText($totals.find('.tm-options-totals .price.amount').text()) || 0;
              const basePrice = currentTotal - bookingPrice.price - optionsTotal;
              if (basePrice > 0) {
                $epoTotals.data('price', basePrice);
              }
            } else {
              const optionsTotal = this.parsePriceFromText($totals.find('.tm-fee-totals .price.amount').text()) ||
                                  this.parsePriceFromText($totals.find('.tm-options-totals .price.amount').text()) || 0;
              const basePrice = currentTotal - optionsTotal;
              if (basePrice > 0) {
                $epoTotals.data('price', basePrice);
              } else {
                $epoTotals.data('price', currentTotal);
              }
            }
          }
        }
      }

      const bookingPrice = this._getBookingPrice();
      if (bookingPrice.price > 0) {
        this._queuePriceUpdate('_onDOMChange', bookingPrice.price, bookingPrice.formatted);
      }
    } catch (error) {
      // Silent fail in production
    }
  },


  _bindFormSubmit($cart) {
    $cart.on('submit', () => {
      const bookingPrice = this._getBookingPrice();
      if (bookingPrice.price > 0) {
        $cart.find('input[name="booking_price"]').remove();
        $cart.append(jQuery('<input>').attr({
          type: 'hidden',
          name: 'booking_price',
          value: bookingPrice.price,
        }));
      }
    });
  },

  _getBookingPrice() {
    const $epoTotals = jQuery(this._selectors.epoTotals);
    let price = 0;
    let formatted = '';

    if ($epoTotals.length) {
      price = parseFloat($epoTotals.data('booking-price')) || 0;
      formatted = $epoTotals.data('booking-price-formatted') || '';
    }

    const $bookingPriceEl = jQuery(this._selectors.bookingPrice);
    if ($bookingPriceEl.length) {
      const priceData = $bookingPriceEl.data('booking_price');
      if (priceData) {
        const parsedPrice = parseFloat(priceData);
        price = Math.max(price, parsedPrice);
      }
    }

    return { price, formatted };
  },

  _queuePriceUpdate(source, bookingPrice, bookingPriceFormatted) {
    const now = Date.now();
    const debounceTime = 100;

    // Remove any pending updates from the same source within debounce time
    this._priceUpdateQueue = this._priceUpdateQueue.filter((item) => {
      return item.source !== source || (now - item.timestamp) > debounceTime;
    });

    // Add new update to queue
    this._priceUpdateQueue.push({
      source,
      bookingPrice: bookingPrice || 0,
      bookingPriceFormatted: bookingPriceFormatted || '',
      timestamp: now,
    });

    // Start processing if not already processing
    if (!this._isProcessingQueue) {
      this._processPriceUpdateQueue();
    }
  },

  _processPriceUpdateQueue() {
    if (this._isProcessingQueue || this._priceUpdateQueue.length === 0) {
      return;
    }

    this._isProcessingQueue = true;

    const update = this._priceUpdateQueue.shift();
    if (!update) {
      this._isProcessingQueue = false;
      if (this._priceUpdateQueue.length > 0) {
        requestAnimationFrame(() => {
          this._processPriceUpdateQueue();
        });
      }
      return;
    }

    // Get the actual booking price (use provided or fetch from DOM)
    let bookingPrice = update.bookingPrice;
    let bookingPriceFormatted = update.bookingPriceFormatted;

    // Always try to get booking price from DOM/data, even if update has a value
    // This ensures we have the latest value
    const bookingPriceData = this._getBookingPrice();
    if (bookingPriceData.price > 0) {
      bookingPrice = bookingPriceData.price;
      if (bookingPriceData.formatted) {
        bookingPriceFormatted = bookingPriceData.formatted;
      }
    }

    const $epoTotals = jQuery(this._selectors.epoTotals);
    if ($epoTotals.length) {
      if (bookingPrice > 0) {
        $epoTotals.data('booking-price', bookingPrice);
        if (bookingPriceFormatted) {
          $epoTotals.data('booking-price-formatted', bookingPriceFormatted);
        }
        this._lastKnownBookingPrice = bookingPrice;
      } else {
        $epoTotals.data('booking-price', 0);
        $epoTotals.data('booking-price-formatted', '');
      }
    }

    this._calculateAndUpdateFinalTotal(bookingPrice, () => {
      this._isProcessingQueue = false;
      if (this._priceUpdateQueue.length > 0) {
        requestAnimationFrame(() => {
          this._processPriceUpdateQueue();
        });
      }
    });
  },

  _getOptionsTotalFromDOM() {
    const $epoTotals = jQuery(this._selectors.epoTotals);
    let optionsTotal = 0;

    const $feesTotal = jQuery(this._selectors.feesTotal);
    if ($feesTotal.length) {
      const feesText = $feesTotal.text();
      if (feesText && feesText.trim()) {
        const parsedFees = this.parsePriceFromText(feesText);
        if (parsedFees > 0.01) {
          optionsTotal = parsedFees;
        }
      }
    }

    if (optionsTotal === 0) {
      const $optionsTotal = jQuery(this._selectors.optionsTotal);
      if ($optionsTotal.length) {
        const optionsText = $optionsTotal.text();
        if (optionsText && optionsText.trim()) {
          optionsTotal = this.parsePriceFromText(optionsText);
        }
      }
    }

    if (optionsTotal === 0) {
      const $epoFields = jQuery(this._selectors.tmepoFields);
      let calculatedFromInputs = 0;
      $epoFields.each(function() {
        const $field = jQuery(this);
        const isCheckbox = $field.is(':checkbox');
        const isRadio = $field.is(':radio');
        let isChecked = $field.is(':checked');
        const isSelect = $field.is('select');
        const selectValue = isSelect ? $field.val() : null;

        if ((isCheckbox || isRadio) && !isChecked) {
          const $container = $field.closest('.tmcp-field-wrap, .tm-epo-field, label');
          if ($container.length) {
            const hasActiveClass = $container.hasClass('active') || $container.hasClass('selected') || $container.hasClass('checked');
            const $checkedInput = $container.find('input:checked, input[checked]');
            if (hasActiveClass || $checkedInput.length > 0) {
              isChecked = true;
            }
          }
        }

        if ((isCheckbox || isRadio) && isChecked) {
          const priceAttr = $field.data('price') || $field.attr('data-price');
          if (priceAttr) {
            calculatedFromInputs += parseFloat(priceAttr) || 0;
          } else {
            const $container = $field.closest('.tmcp-field-wrap, .tm-epo-field');
            const $priceElement = $container.find('.tm-epo-field-price, .price, [data-price]');
            if ($priceElement.length) {
              const priceText = $priceElement.text() || $priceElement.attr('data-price') || $priceElement.data('price');
              if (priceText) {
                calculatedFromInputs += this.parsePriceFromText(priceText);
              }
            }
          }
        } else if (isSelect && selectValue && selectValue !== '') {
          const $selectedOption = $field.find('option:selected');
          const priceAttr = $selectedOption.data('price') || $selectedOption.attr('data-price');
          if (priceAttr) {
            calculatedFromInputs += parseFloat(priceAttr) || 0;
          } else {
            const priceText = $selectedOption.text();
            if (priceText) {
              calculatedFromInputs += this.parsePriceFromText(priceText);
            }
          }
        }
      }.bind(this));

      if (calculatedFromInputs > 0.01) {
        optionsTotal = calculatedFromInputs;
      }
    }

    if (optionsTotal === 0 && $epoTotals.length) {
      const storedOptions = parseFloat($epoTotals.data('options-total')) || 0;
      if (storedOptions > 0) {
        const $epoFields = jQuery(this._selectors.tmepoFields);
        let hasSelectedOptions = false;
        $epoFields.each(function() {
          const $field = jQuery(this);
          if (($field.is(':checkbox') || $field.is(':radio')) && $field.is(':checked')) {
            hasSelectedOptions = true;
            return false;
          } else if ($field.is('select') && $field.val() && $field.val() !== '') {
            hasSelectedOptions = true;
            return false;
          }
        });
        if (hasSelectedOptions) {
          optionsTotal = storedOptions;
        }
      }
    }

    if (optionsTotal > 0 && $epoTotals.length) {
      $epoTotals.data('options-total', optionsTotal);
      this._lastKnownOptionsTotal = optionsTotal;
    }

    return optionsTotal;
  },

  _calculateFinalTotal(bookingPrice) {
    const $epoTotals = jQuery(this._selectors.epoTotals);

    let basePrice = parseFloat($epoTotals.data('price')) || 0;
    const optionsTotal = this._getOptionsTotalFromDOM();

    // If bookingPrice exists, it contains the total price (base + booking period)
    // So basePrice should be 0 when bookingPrice > 0
    if (bookingPrice > 0) {
      basePrice = 0;
      const finalTotal = bookingPrice + optionsTotal;
      return {
        basePrice: 0,
        optionsTotal,
        finalTotal,
      };
    }

    // If no bookingPrice, we need to determine the basePrice
    // If no basePrice stored, try to calculate it from current finalTotal
    if (basePrice === 0) {
      const $finalTotal = jQuery(this._selectors.finalTotal);
      if ($finalTotal.length) {
        const currentFinalTotal = this.parsePriceFromText($finalTotal.text());
          if (currentFinalTotal > 0) {
            basePrice = currentFinalTotal - optionsTotal;
            if (basePrice > 0 && basePrice <= currentFinalTotal) {
            $epoTotals.data('price', basePrice);
            this._lastKnownBasePrice = basePrice;
          } else if (currentFinalTotal > 0) {
            basePrice = currentFinalTotal;
            $epoTotals.data('price', basePrice);
            this._lastKnownBasePrice = basePrice;
          }
        }
      }
    } else {
      this._lastKnownBasePrice = basePrice;
    }

    const finalTotal = basePrice + optionsTotal;

    return {
      basePrice,
      optionsTotal,
      finalTotal,
    };
  },

  _calculateAndUpdateFinalTotal(bookingPrice, callback) {
    const maxRetries = 3;
    let retryCount = 0;

    const attemptUpdate = () => {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      let $totals = jQuery(this._selectors.totals);
      let $finalTotal = jQuery(this._selectors.finalTotal);

      if (!$totals.length && $epoTotals.length) {
        this._ensureTotalsContainer($epoTotals);
        $totals = jQuery(this._selectors.totals);
        $finalTotal = jQuery(this._selectors.finalTotal);
      }

      if (!$finalTotal.length) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(attemptUpdate, 100);
          return;
        } else {
          if (callback) callback();
          return;
        }
      }

      if ($totals.length) {
        this._ensureFinalTotalVisible($totals);
      }

      if (bookingPrice > 0) {
        const bookingPriceFormatted = $epoTotals.data('booking-price-formatted') || this.formatPrice(bookingPrice);
        this.restoreBookingPriceInTotals(bookingPrice, bookingPriceFormatted);
      }

      const calculation = this._calculateFinalTotal(bookingPrice);
      const formattedFinalTotal = this.formatPrice(calculation.finalTotal);
      const currentText = $finalTotal.text().replace(/€\s*/g, '').trim();

      if (currentText !== formattedFinalTotal) {
        const finalTotalEl = $finalTotal[0];
        if (finalTotalEl) {
          finalTotalEl.textContent = formattedFinalTotal.replace(/<[^>]*>/g, '') || formattedFinalTotal;
          if (formattedFinalTotal.includes('<')) {
            finalTotalEl.innerHTML = formattedFinalTotal;
          }
        } else {
          $finalTotal.html(formattedFinalTotal);
        }

        if ($totals.length) {
          this._ensureFinalTotalVisible($totals);
        }
      }

      if (callback) callback();
    };

    attemptUpdate();
  },

  updateBookingPriceInTotals(response) {
    // Simplified: only parse and store booking price, then queue update
    const $epoTotals = jQuery(this._selectors.epoTotals);
    if (!$epoTotals.length) {
      return;
    }

    const previousBookingPrice = parseFloat($epoTotals.data('booking-price')) || 0;
    const hasBookingDates = this._hasBookingDates();

    let bookingPriceRaw = 0;
    let bookingPriceFormatted = '';

    if (response && response.fragments) {
      const fragments = response.fragments;
      bookingPriceRaw = parseFloat(fragments.booking_price) || 0;
      bookingPriceFormatted = fragments.booking_price_formatted || '';

      if (!bookingPriceFormatted && bookingPriceRaw > 0) {
        bookingPriceFormatted = this.formatPrice(bookingPriceRaw);
      }

      // If booking price is 0 but we have dates and previous price, keep previous price
      if (bookingPriceRaw === 0 && hasBookingDates && previousBookingPrice > 0) {
        bookingPriceRaw = previousBookingPrice;
        bookingPriceFormatted = $epoTotals.data('booking-price-formatted') || this.formatPrice(previousBookingPrice);
      }

      if (bookingPriceRaw > 0 && $epoTotals.length) {
        $epoTotals.data('booking-price', bookingPriceRaw);
        if (bookingPriceFormatted) {
          $epoTotals.data('booking-price-formatted', bookingPriceFormatted);
        }
      } else if (bookingPriceRaw === 0) {
        $epoTotals.data('booking-price', 0);
        $epoTotals.data('booking-price-formatted', '');
      }
    } else if (hasBookingDates && previousBookingPrice > 0) {
      // No response but we have dates and previous price, keep it
      bookingPriceRaw = previousBookingPrice;
      bookingPriceFormatted = $epoTotals.data('booking-price-formatted') || this.formatPrice(previousBookingPrice);
      $epoTotals.data('booking-price', previousBookingPrice);
    } else {
      $epoTotals.data('booking-price', 0);
      $epoTotals.data('booking-price-formatted', '');
    }

    // Store basePrice from response if available
    if (response && response.fragments && response.fragments.epo_base_price) {
      const basePriceFromResponse = parseFloat(response.fragments.epo_base_price) || 0;
      if (basePriceFromResponse > 0) {
        $epoTotals.data('price', basePriceFromResponse);
      }
    }

    // Queue the update
    if (bookingPriceRaw > 0) {
      this._queuePriceUpdate('updateBookingPriceInTotals', bookingPriceRaw, bookingPriceFormatted);
    }
  },


  _verifyAndRestorePrice(bookingPrice) {
    if (this._isProcessingQueue) {
      return;
    }

    const now = Date.now();
    const lastVerify = this._lastVerifyTime || 0;
    const lastVerifyPrice = this._lastVerifyPrice || 0;

    if (now - lastVerify < 10 && Math.abs(bookingPrice - lastVerifyPrice) < 0.01) {
      return;
    }

    this._lastVerifyTime = now;
    this._lastVerifyPrice = bookingPrice;

    this._queuePriceUpdate('_verifyAndRestorePrice', bookingPrice, '');
  },



  restoreBookingPriceInTotals(bookingPrice, bookingPriceFormatted) {
    const $totals = jQuery(this._selectors.totals);
    if (!$totals.length) {
      return;
    }

    if (bookingPrice > 0) {
      this._ensureBookingPriceRow($totals, bookingPriceFormatted || this.formatPrice(bookingPrice));
    }

    this._hidePriceRows($totals);
    this._ensureFinalTotalVisible($totals);
  },

  _ensureFinalTotalVisible($totals) {
    const $epoTotals = jQuery(this._selectors.epoTotals);
    if ($epoTotals.length) {
      $epoTotals[0].style.display = 'block';
    }

    $totals.show();
    this._ensureOptionsRowsVisible($totals);

    const $finalTotalDT = $totals.find('dt.tm-final-totals');
    const $finalTotalDD = $totals.find('dd.tm-final-totals');

    if ($finalTotalDT.length) {
      $finalTotalDT[0].style.display = '';
      $finalTotalDT.show();
      $finalTotalDT.css('display', '');
    }
    if ($finalTotalDD.length) {
      $finalTotalDD[0].style.display = '';
      $finalTotalDD.show();
      $finalTotalDD.css('display', '');

      const $finalTotalRow = $finalTotalDD.closest('dd').parent();
      if ($finalTotalRow.length) {
        $finalTotalRow.show();
        $finalTotalRow.css('display', '');
      }
    }

    const $finalTotal = jQuery(this._selectors.finalTotal);
    if ($finalTotal.length) {
      const $finalTotalParent = $finalTotal.closest('dd.tm-final-totals');
      if ($finalTotalParent.length) {
        $finalTotalParent.show();
        $finalTotalParent.css('display', '');
        const $finalTotalRow = $finalTotalParent.parent();
        if ($finalTotalRow.length) {
          $finalTotalRow.show();
          $finalTotalRow.css('display', '');
        }
      }
    }
  },

  _ensureOptionsRowsVisible($totals) {
    let $feesDT = $totals.find('dt.tm-fee-totals');
    let $feesDD = $totals.find('dd.tm-fee-totals');

    if (!$feesDT.length) {
      $feesDT = jQuery('.tm-fee-totals').filter('dt');
    }
    if (!$feesDD.length) {
      $feesDD = jQuery('.tm-fee-totals').filter('dd');
    }

    // Only show extra options row if there's actually a value > 0
    // Check both DOM value and stored value to avoid hiding during DOM updates
    if ($feesDT.length && $feesDD.length) {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      const storedOptionsTotal = parseFloat($epoTotals.data('options-total')) || 0;
      const optionsValue = this.parsePriceFromText($feesDD.find('.price.amount').text()) || 0;
      // Use stored value if DOM value is 0 but stored value > 0 (DOM might be updating)
      const effectiveValue = optionsValue > 0 ? optionsValue : storedOptionsTotal;
      if (effectiveValue > 0) {
        $feesDT.show().css('display', '');
        $feesDD.show().css('display', '');
      } else {
        $feesDT.hide();
        $feesDD.hide();
      }
    } else if ($feesDT.length || $feesDD.length) {
      if ($feesDD.length) {
        const $epoTotals = jQuery(this._selectors.epoTotals);
        const storedOptionsTotal = parseFloat($epoTotals.data('options-total')) || 0;
        const optionsValue = this.parsePriceFromText($feesDD.find('.price.amount').text()) || 0;
        const effectiveValue = optionsValue > 0 ? optionsValue : storedOptionsTotal;
        if (effectiveValue > 0) {
          $feesDT.show().css('display', '');
          $feesDD.show().css('display', '');
        } else {
          $feesDT.hide();
          $feesDD.hide();
        }
      } else {
        $feesDT.hide();
      }
    }
  },

  _createOptionsTotalRow($totals, optionsTotal) {
    const now = Date.now();
    if (this._lastOptionsTotal === optionsTotal && (now - this._lastOptionsTotalTime) < 500) {
      const $existingFeesDD = $totals.find('dd.tm-fee-totals');
      if ($existingFeesDD.length) {
        const existingPrice = this.parsePriceFromText($existingFeesDD.find('.price.amount').text());
        if (Math.abs(existingPrice - optionsTotal) < 0.01) {
          return;
        }
      }
    }
    this._lastOptionsTotal = optionsTotal;
    this._lastOptionsTotalTime = now;

    const $existingOptionsRows = $totals.find('.tm-options-totals').closest('dd').parent();
    if ($existingOptionsRows.length) {
      $existingOptionsRows.remove();
    }

    if (optionsTotal <= 0) {
      $totals.find('.tm-fee-totals').closest('dd').parent().remove();
      return;
    }

    const formattedOptionsTotal = this.formatPrice(optionsTotal);

    let $existingFeesDT = $totals.find('dt.tm-fee-totals');
    let $existingFeesDD = $totals.find('dd.tm-fee-totals');

    if (!$existingFeesDD.length) {
      $existingFeesDD = jQuery('dd.tm-fee-totals').filter(function() {
        return jQuery(this).closest('.tm-extra-product-options-totals').length > 0;
      });
    }
    if (!$existingFeesDT.length) {
      $existingFeesDT = jQuery('dt.tm-fee-totals').filter(function() {
        return jQuery(this).closest('.tm-extra-product-options-totals').length > 0;
      });
    }

    if ($existingFeesDD.length && $existingFeesDT.length) {
      const existingPrice = this.parsePriceFromText($existingFeesDD.find('.price.amount').text());
      if (Math.abs(existingPrice - optionsTotal) < 0.01) {
        $existingFeesDD.find('.price.amount').html(formattedOptionsTotal);
        return;
      }
    }

    const $allFeesDT = jQuery('dt.tm-fee-totals').filter(function() {
      return jQuery(this).closest('.tm-extra-product-options-totals').length > 0;
    });
    const $allFeesDD = jQuery('dd.tm-fee-totals').filter(function() {
      return jQuery(this).closest('.tm-extra-product-options-totals').length > 0;
    });

    if ($allFeesDT.length || $allFeesDD.length) {
      $allFeesDT.remove();
      $allFeesDD.remove();
    }

    const $dt = jQuery('<dt class="tm-fee-totals">Extra opties</dt>');
    const $dd = jQuery('<dd class="tm-fee-totals"><span class="price amount fees"></span></dd>');
    $dd.find('.price.amount').html(formattedOptionsTotal);

    let $dl = jQuery(this._selectors.totalsDL);
    if (!$dl.length) {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      if ($epoTotals.length) {
        $dl = $epoTotals.find('dl.tm-extra-product-options-totals').first();
      }
      if (!$dl.length) {
        $dl = $totals.is('dl') ? $totals : $totals.find('dl').first();
      }
    }

    if (!$dl.length) {
      return;
    }

    const $finalTotalDT = $dl.find('dt.tm-final-totals').first();

    if ($finalTotalDT.length) {
      $dt.insertBefore($finalTotalDT);
      $dd.insertAfter($dt);
    } else {
      $dl.append($dt);
      $dl.append($dd);
    }

    const $existingOptionsRow = $totals.find('.tm-options-totals').closest('dd').parent();
    if ($existingOptionsRow.length) {
      $existingOptionsRow.remove();
    }
  },

  _hidePriceRows($totals) {
    // Only hide unit price, NOT booking price - booking price should always be visible
    const rowsToHide = ['.tm-unit-price'];
    rowsToHide.forEach((selector) => {
      const $row = $totals.find(selector).closest('dd').parent();
      if ($row.length) {
        $row.hide();
      }
    });
  },

  _ensureBookingPriceRow($totals, bookingPriceFormatted) {
    let $bookingRow = $totals.find('.tm-booking-price').closest('dd').parent();

    if (!$bookingRow.length) {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      if ($epoTotals.length) {
        $bookingRow = $epoTotals.find('.tm-booking-price').closest('dd').parent();
      }
    }

    if (!$bookingRow.length) {
      $bookingRow = jQuery('.tm-booking-price').closest('dd').parent().filter(function() {
        const $row = jQuery(this);
        return $row.closest('.tm-extra-product-options-totals').length > 0 ||
               $row.closest('#tm-epo-totals').length > 0;
      });
    }

    if ($bookingRow.length) {
      const $priceEl = $bookingRow.find('.tm-booking-price .price.amount, .price.amount.booking');
      if ($priceEl.length) {
        const currentPrice = $priceEl.text().replace(/€\s*/g, '').trim();
        const newPrice = bookingPriceFormatted.replace(/€\s*/g, '').trim();
        if (currentPrice !== newPrice) {
          $priceEl.html(bookingPriceFormatted);
        }
      } else {
        const $dd = $bookingRow.find('dd.tm-booking-price');
        if ($dd.length) {
          $dd.append(jQuery('<span class="price amount booking"></span>').html(bookingPriceFormatted));
        }
      }
      $bookingRow.hide();
      return;
    }

    const $dt = jQuery('<dt class="tm-booking-price" style="display: none;">Huurprijs</dt>');
    const $dd = jQuery('<dd class="tm-booking-price" style="display: none;"><span class="price amount booking"></span></dd>');
    $dd.find('.price.amount').html(bookingPriceFormatted);

    let $dl = $totals.is('dl') ? $totals : $totals.find('dl').first();
    if (!$dl.length) {
      const $epoTotals = jQuery(this._selectors.epoTotals);
      if ($epoTotals.length) {
        $dl = $epoTotals.find('dl.tm-extra-product-options-totals').first();
      }
    }

    if ($dl.length) {
      const $finalTotal = $dl.find('.tm-final-totals').closest('dd').parent();
      if ($finalTotal.length) {
        $finalTotal.before($dd).before($dt);
      } else {
        $dl.append($dt);
        $dl.append($dd);
      }
    } else {
      const $finalTotal = $totals.find('.tm-final-totals').closest('dd').parent();
      if ($finalTotal.length) {
        $finalTotal.before($dd).before($dt);
      } else {
        $totals.prepend($dd).prepend($dt);
      }
    }
  },

  _removeBookingPriceRow($totals) {
    const $bookingRow = $totals.find('.tm-booking-price').closest('dd').parent();
    if ($bookingRow.length) {
      $bookingRow.remove();
    }
  },

  _hasBookingDates() {
    const $cart = jQuery(this._selectors.cart);
    if (!$cart.length) {
      return false;
    }

    const startDateSelectors = [
      'input[name="start_date"]',
      'input[name="start"]',
      'input[name="wceb_start"]',
      'input[name="_start_date"]',
      '#start_date',
      '#start',
      '.wceb_picker_wrap input[name*="start"]',
      '.wceb_picker_wrap input[id*="start"]',
      '[data-name*="start"]',
    ];

    const endDateSelectors = [
      'input[name="end_date"]',
      'input[name="end"]',
      'input[name="wceb_end"]',
      'input[name="_end_date"]',
      '#end_date',
      '#end',
      '.wceb_picker_wrap input[name*="end"]',
      '.wceb_picker_wrap input[id*="end"]',
      '[data-name*="end"]',
    ];

    let hasStart = false;
    let hasEnd = false;

    startDateSelectors.forEach((selector) => {
      if (hasStart) {
        return;
      }
      try {
        const $field = $cart.find(selector).first();
        if ($field.length) {
          const val = $field.val();
          if (val && typeof val === 'string' && val.trim()) {
            hasStart = true;
          }
        }
      } catch (e) {
        // Silent fail for invalid selectors
      }
    });

    endDateSelectors.forEach((selector) => {
      if (hasEnd) {
        return;
      }
      try {
        const $field = $cart.find(selector).first();
        if ($field.length) {
          const val = $field.val();
          if (val && typeof val === 'string' && val.trim()) {
            hasEnd = true;
          }
        }
      } catch (e) {
        // Silent fail for invalid selectors
      }
    });

    return hasStart && hasEnd;
  },

  parsePriceFromText(text) {
    if (!text) {
      return 0;
    }
    return parseFloat(text.replace(/€\s*/g, '').replace(/[^\d,.-]/g, '').replace(',', '.')) || 0;
  },

  formatPrice(price) {
    if (typeof window.wc_price === 'function') {
      const formatted = window.wc_price(price);
      if (formatted && formatted.trim()) {
        return formatted.replace(/€/g, '').replace(/\s+/g, ' ').trim();
      }
    }
    return parseFloat(price).toFixed(2).replace('.', ',');
  },
};

export default product;



// WEBPACK FOOTER //
// ./scripts/routes/product.js